// The space train (art guide §9, Godot train brief, CLAUDE.md gotchas 14 + 15).
//
// A 26-vehicle steampunk sky-dreadnought: verdigris + copper + brass, ONE cream livery band
// end to end, glowing finned thruster pods where the wheels were, steam from the stack and
// the joints. Each car is a separate rigid body posed every frame on a banking 3D
// figure-eight, sampling the path at its OWN two coupling points, so neighbours always meet
// at the joint (kink < ~2°, coupler gap 0). Cars roll into the bank through a lagging
// spring, so the rake snakes and leans like something heavy.
//
// Draw calls (q2/q3): hull hi-LOD 1 + hull lo-LOD 2 (the cars before/after the near span)
// + thruster/searchlight cones 1 + glow sprites 1 + steam 1 = 6. q1: lo-LOD only, 12 cars,
// 4 draws. q0: the world doesn't build the train (index.ts).
// The whole consist is ONE merged geometry per LOD: each vertex carries its car slot
// (aCar), and the vertex shader poses it from per-car position/quaternion uniforms, so
// repeated car types cost no extra draws. Detail (riveted plates, patina streaks, livery
// lettering, mullions) is procedural in the fragment shader, in car-local space.

import * as THREE from 'three';
import type { ViewCtx } from '../contracts';
import { HAZE, withHaze } from '../materials';
import { PAL } from '../palette';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { budget, sharedMade, sharedTexture } from '../warm';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { CAR_LEN, CONSIST, PAINTS, TRAIN_SCALE, type CarType, type Emitter, type FxOut, type Vent } from './train_cars';
import { TrainPath } from './train_path';

// Speed profile: the rake cruises at V_NEAR while any of it is near the arena pass, then runs at
// V_FAR round the far side of the loop (hidden behind the ranges, deep in the haze), so the
// near pass comes round every ~80 s instead of once per 4.5-minute lap.
const V_NEAR = 24, V_FAR = 200; // m/s
const SLOW_BEFORE = 250, SLOW_AFTER = 120, RAMP = 320; // m of head travel around the near point
// the loco passed the near point this many seconds BEFORE the world appears (negative = the
// rake is already crossing the arrival view at spawn, so even a slow first minute shows it)
const T_FIRST = -4;
const TAB_DT = 1 / 20;
let trainGlb: Promise<THREE.Object3D> | null = null;
/** built rakes, per consist (see the Train constructor) */
const carsCache = new Map<string, { lo: Built; hi: Built | null; fx: FxOut }>();
/** a geometry of its own (draw range) over a built rake's shared attributes */
function viewOf(b: Built): Built {
  const g = new THREE.BufferGeometry();
  for (const k of Object.keys(b.geo.attributes)) g.setAttribute(k, b.geo.attributes[k]);
  g.setIndex(b.geo.index);
  g.boundingSphere = b.geo.boundingSphere;
  return { geo: g, start: b.start, end: b.end };
}
const MAX_CARS = 26;
/** near pass: metres beyond the arena centre, metres above its floor */
export const NEAR_D = 132, NEAR_Y = 34, PATH_K = 1.6;

// ------------------------------------------------------------------ shaders
const POSE_GLSL = /* glsl */ `
uniform vec4 uCarP[${MAX_CARS}];
uniform vec4 uCarQ[${MAX_CARS}];
vec3 qrot( vec4 q, vec3 v ) { return v + 2.0 * cross( q.xyz, cross( q.xyz, v ) + q.w * v ); }
`;

const NOISE_GLSL = /* glsl */ `
float h13( vec3 p ) { p = fract( p * 0.1031 ); p += dot( p, p.zyx + 31.32 ); return fract( ( p.x + p.y ) * p.z ); }
float vnoise( vec3 p ) {
  vec3 i = floor( p ), f = fract( p ); f = f * f * ( 3.0 - 2.0 * f );
  return mix( mix( mix( h13( i ), h13( i + vec3( 1, 0, 0 ) ), f.x ), mix( h13( i + vec3( 0, 1, 0 ) ), h13( i + vec3( 1, 1, 0 ) ), f.x ), f.y ),
              mix( mix( h13( i + vec3( 0, 0, 1 ) ), h13( i + vec3( 1, 0, 1 ) ), f.x ), mix( h13( i + vec3( 0, 1, 1 ) ), h13( i + vec3( 1, 1, 1 ) ), f.x ), f.y ), f.z );
}
float fbm3( vec3 p ) { return vnoise( p ) * 0.55 + vnoise( p * 2.07 + 7.3 ) * 0.3 + vnoise( p * 4.3 + 1.7 ) * 0.15; }
`;

// The hull is a MeshStandardMaterial (real GGX specular, metalness, env reflections) patched in
// place: the vertex stage poses each car from the uniform arrays, the fragment stage paints the
// procedural plating / livery / windows, reads the baked AO+edge atlas and the object-space
// normal atlas (hd rivets/bolts baked onto the runtime mesh), and adds a procedural seam/rivet bump.
const HULL_VERT_DECL = /* glsl */ `
${POSE_GLSL}
attribute vec4 aMat;
attribute float aCar;
varying vec3 vLocal;
varying vec3 vNl;
varying vec4 vMat;
varying float vCar;
varying vec2 vTUv;
varying vec4 vCarQ;
`;
const HULL_BEGINNORMAL = /* glsl */ `
int ci = int( aCar + 0.5 );
vec4 cq = uCarQ[ ci ];
vec3 objectNormal = qrot( cq, normal );
vNl = normal; vCarQ = cq;
`;
const HULL_BEGIN = /* glsl */ `
vec3 transformed = qrot( cq, position ) + uCarP[ ci ].xyz;
vLocal = position; vMat = aMat; vCar = aCar; vTUv = uv;
`;

const HULL_FRAG_DECL = /* glsl */ `
${NOISE_GLSL}
uniform float uTimeT;
uniform vec3 uSunDirT;
uniform sampler2D uAtlas;
uniform sampler2D uNrm;
varying vec3 vLocal;
varying vec3 vNl;
varying vec4 vMat;
varying float vCar;
varying vec2 vTUv;
varying vec4 vCarQ;
vec3 qrotF( vec4 q, vec3 v ) { return v + 2.0 * cross( q.xyz, cross( q.xyz, v ) + q.w * v ); }
vec3 toLin( vec3 c ) { return pow( c, vec3( 2.2 ) ); }
vec3 bumpArb( vec3 surf_pos, vec3 surf_norm, vec2 dHdxy, float fd ) {
  // far, tiny triangles can give zero derivatives: normalize(0) is NaN on Metal/Safari, and one NaN
  // pixel blooms into black squares through the mip chain. Fall back to the plain normal there.
  vec3 dx = dFdx( surf_pos ), dy = dFdy( surf_pos );
  if ( dot( dx, dx ) < 1e-14 || dot( dy, dy ) < 1e-14 ) return surf_norm;
  vec3 sx = normalize( dx ), sy = normalize( dy );
  vec3 R1 = cross( sy, surf_norm ), R2 = cross( surf_norm, sx );
  float det = dot( sx, R1 ) * fd;
  vec3 g = sign( det ) * ( dHdxy.x * R1 + dHdxy.y * R2 );
  vec3 n = abs( det ) * surf_norm - g;
  return dot( n, n ) > 1e-12 ? normalize( n ) : surf_norm;
}
// 3x5 bitmap glyphs: G U L P . and digits
int glyphBits( int g ) {
  if ( g == 0 ) return 31599;
  if ( g == 1 ) return 11415;
  if ( g == 2 ) return 29671;
  if ( g == 3 ) return 29647;
  if ( g == 4 ) return 23497;
  if ( g == 5 ) return 31183;
  if ( g == 6 ) return 31215;
  if ( g == 7 ) return 29257;
  if ( g == 8 ) return 31727;
  if ( g == 9 ) return 31695;
  if ( g == 10 ) return 31087; // G
  if ( g == 11 ) return 23407; // U
  if ( g == 12 ) return 18727; // L
  if ( g == 13 ) return 31716; // P
  if ( g == 14 ) return 2; // .
  return 0;
}
float glyph( int g, vec2 c ) {
  if ( c.x < 0.0 || c.x >= 1.0 || c.y < 0.0 || c.y >= 1.0 ) return 0.0;
  int col = int( c.x * 3.0 ), row = 4 - int( c.y * 5.0 );
  int bit = 14 - ( row * 3 + col );
  return float( ( glyphBits( g ) >> bit ) & 1 );
}
`;

// replaces <color_fragment>: albedo, metal/rough, AO, emissive and the bump height, all at main scope
const HULL_SURFACE = /* glsl */ `
vec3 albedo = toLin( vColor.rgb );
float emis = vMat.x, patina = vMat.z;
float mr = floor( vMat.y * 255.0 + 0.5 );
float tMetal = floor( mr / 16.0 ) / 15.0, tRough = mod( mr, 16.0 ) / 15.0;
float shine = tMetal * ( 1.0 - tRough );
bool isBrass = tMetal > 0.95 && tRough < 0.5;
float det = vMat.w * 255.0;
vec3 Nl = normalize( vNl );
vec3 an = abs( Nl );
vec2 puv = an.x > an.y && an.x > an.z ? vec2( vLocal.z * -sign( Nl.x ), vLocal.y ) : ( an.y > an.z ? vLocal.zx : vLocal.xy );
float aa = length( fwidth( vLocal ) );
float fine = 1.0 - smoothstep( 0.05, 0.22, aa );
float carSeed = vCar * 7.13;
bool hasUv = vTUv.x > -0.5;
vec2 bk = hasUv ? texture2D( uAtlas, vTUv ).rg : vec2( 1.0, 0.0 );
float bao = mix( 1.0, bk.x, 0.9 );
float edge = bk.y;
float n1 = fbm3( vLocal * 0.55 + carSeed );
float streak = fbm3( vec3( vLocal.x * 2.6, vLocal.y * 0.28, vLocal.z * 2.6 ) + 11.0 + carSeed );
float crev = smoothstep( 0.92, 0.45, bao );
// oxidised steel darkens and cools; verdigris and grime pool in crevices and drip below them
float pat = patina * clamp( crev * 0.9 + smoothstep( 0.6, 0.9, streak * 0.8 + n1 * 0.3 ) * 0.35, 0.0, 1.0 );
vec3 verdCol = toLin( vec3( 0.46, 0.60, 0.55 ) );
albedo = mix( albedo, verdCol * ( 0.75 + 0.3 * n1 ), pat * ( isBrass ? 0.45 : 0.68 ) );
// weathered metal, not toy paint: pull the paint toward grey, then mottle it with pale
// verdigris blooms (large scale, so they survive at range) and rust-dark run-off streaks
if ( emis <= 0.0 ) {
  float lum0 = dot( albedo, vec3( 0.299, 0.587, 0.114 ) );
  albedo = mix( vec3( lum0 ), albedo, tMetal < 0.5 ? 0.9 : 0.62 );
  float blot = smoothstep( 0.38, 0.66, fbm3( vLocal * vec3( 0.2, 0.34, 0.2 ) + carSeed + 5.0 ) );
  float drip = smoothstep( 0.55, 0.8, fbm3( vec3( vLocal.x * 3.1, vLocal.y * 0.18, vLocal.z * 3.1 ) + 23.0 + carSeed ) );
  vec3 verdPale = toLin( vec3( 0.52, 0.60, 0.55 ) );
  bool isCopper = tMetal > 0.5 && !isBrass;
  albedo = mix( albedo, verdPale * ( 0.85 + 0.25 * n1 ), patina * ( blot * ( isCopper ? 0.6 : 0.72 ) + drip * 0.16 ) );
  if ( isCopper ) albedo *= mix( 0.72, 1.0, streak ) * ( 1.0 - drip * 0.25 );
  else albedo *= 1.0 - drip * 0.22 * patina;
  tRough = clamp( tRough + blot * patina * 0.25, 0.18, 1.0 );
  tMetal *= 1.0 - blot * patina * 0.5;
}
albedo *= mix( 1.0, 0.35, crev * 0.85 );
albedo *= 0.86 + 0.28 * n1;
tRough = clamp( tRough + pat * 0.3 + ( n1 - 0.5 ) * 0.18 + crev * 0.2, 0.18, 1.0 );
tMetal *= 1.0 - pat * 0.6;
// raised edges wear through to bright, polished copper/brass
float wear = smoothstep( 0.18, 0.6, edge ) * smoothstep( 0.5, 0.85, bao ) * ( 0.55 + 0.45 * n1 );
if ( emis <= 0.0 ) {
  vec3 bare = isBrass ? albedo * 1.3 : toLin( vec3( 0.80, 0.56, 0.38 ) );
  // edge wear is a close-up read; at range the blurred edge mask would gild whole panels
  float wk = wear * ( det > 32.0 && det < 96.0 ? 0.85 : 0.6 ) * ( 1.0 - smoothstep( 0.04, 0.16, aa ) );
  albedo = mix( albedo, bare, wk );
  tMetal = mix( tMetal, 1.0, wk );
  tRough = mix( tRough, 0.25, wk );
}
if ( emis <= 0.0 && vCar > 0.5 && tMetal < 0.5 && det > 32.0 && det < 96.0 ) {
  float lt = dot( albedo, vec3( 0.299, 0.587, 0.114 ) );
  albedo = max( vec3( lt ) + ( albedo - vec3( lt ) ) * 1.9, 0.0 ) * vec3( 0.92, 1.04, 1.06 );
}
float tBumpH = 0.0;
if ( det > 32.0 && det < 96.0 ) {
  // riveted plates, staggered courses: tone per plate, grime at each plate's lower lip, grooved seams
  vec2 g = puv / vec2( 2.3, 1.1 );
  g.x += mod( floor( g.y ), 2.0 ) * 0.5;
  vec2 id = floor( g ), f = fract( g );
  float dx = min( f.x, 1.0 - f.x ) * 2.3, dy = min( f.y, 1.0 - f.y ) * 1.1;
  float sd = min( dx, dy );
  float w = 0.035 + aa * 0.9;
  float seam = ( 1.0 - smoothstep( w * 0.4, w, sd ) );
  float ph = h13( vec3( id, carSeed ) );
  // no per-plate tone: the hull reads as one weathered skin with soft blotches, not tiles
  tRough = clamp( tRough + ( ph - 0.5 ) * 0.08, 0.18, 1.0 );
  albedo *= 1.0 - seam * 0.28 * fine;
  float ru = fract( puv.x / 0.24 ) - 0.5;
  float rv = dy - 0.075;
  float rd = length( vec2( ru * 0.24, rv ) );
  float rivet = ( 1.0 - smoothstep( 0.03, 0.05 + aa * 0.3, rd ) );
  float rv2 = fract( puv.y / 0.24 ) - 0.5;
  float rd2 = length( vec2( dx - 0.075, rv2 * 0.24 ) );
  rivet = max( rivet, 1.0 - smoothstep( 0.03, 0.05 + aa * 0.3, rd2 ) );
  float micro = fbm3( vec3( puv * 5.3, carSeed ) );
  float scratch = smoothstep( 0.62, 0.7, fbm3( vec3( puv.x * 0.9, puv.y * 11.0, carSeed + 3.0 ) ) );
  albedo *= 0.88 + 0.24 * micro;
  albedo = mix( albedo, albedo * 1.35 + 0.02, scratch * fine * 0.5 );
  tRough = clamp( tRough + ( micro - 0.5 ) * 0.25 - scratch * 0.2, 0.15, 1.0 );
  tBumpH = ( -seam * 0.6 + rivet * 0.5 + ( micro - 0.5 ) * 0.15 ) * fine;
  albedo = mix( albedo, toLin( vec3( 0.62, 0.52, 0.36 ) ), rivet * 0.7 * fine );
  tMetal = mix( tMetal, 1.0, rivet * 0.6 * fine );
}
if ( emis <= 0.0 && det > 32.0 && det < 96.0 ) {
  // COARSE breakup that must survive 129 m (the fine plate work above is sub-pixel there):
  // big panel courses with dark seams, a grimy-low to clean-high value ramp, the odd replaced
  // plate in another teal or in copper, and rust drips running down from the rivet rows
  vec2 cg = vec2( puv.x, ( vLocal.y + 2.8 ) / 1.2 );
  cg.x += mod( floor( cg.y ), 2.0 ) * 0.5;
  vec2 cid = floor( cg ), cf = fract( cg );
  float cd = min( min( cf.x, 1.0 - cf.x ), min( cf.y, 1.0 - cf.y ) * 1.2 );
  float cseam = 1.0 - smoothstep( 0.03, 0.06 + aa * 0.6, cd );
  // hull barrels only: pods, domes and the tower stay smooth (a grid there reads as scales)
  float onHull = ( 1.0 - smoothstep( 4.2, 5.2, vLocal.y ) ) * smoothstep( 0.45, 0.7, an.x );
  // above the hull (pods, domes, tower) only vertical seams every ~3.8 m: plated drums, not scales
  float cu = fract( puv.x * 0.5 );
  float useam = ( 1.0 - smoothstep( 0.015, 0.03 + aa * 0.3, min( cu, 1.0 - cu ) ) ) * smoothstep( 0.45, 0.7, an.x );
  cseam = mix( useam * 0.8, cseam, onHull );
  float chash = h13( vec3( cid, carSeed + 9.0 ) );
  if ( tMetal < 0.5 ) {
    if ( an.x < 0.5 ) {}
    else if ( chash > 0.88 ) albedo = mix( albedo, toLin( vec3( 0.44, 0.58, 0.50 ) ) * ( 0.85 + 0.3 * n1 ), 0.75 );
    else if ( chash > 0.8 ) albedo *= 0.68;
    else if ( chash < 0.05 ) { albedo = mix( albedo, toLin( vec3( 0.56, 0.34, 0.24 ) ), 0.8 ); tMetal = 0.55; }
  }
  albedo *= 1.0 - cseam * 0.4;
  albedo *= mix( 0.72, 1.12, smoothstep( -2.6, 3.6, vLocal.y ) );
  float dcol = h13( vec3( floor( puv.x / 0.2 ), carSeed, 3.0 ) );
  float dL = 0.35 + 1.3 * fract( dcol * 7.3 );
  float rust = 0.0;
  for ( int k = 0; k < 3; k++ ) {
    float ry = k == 0 ? 1.15 : ( k == 1 ? -0.6 : -1.5 );
    float dy = ry - vLocal.y;
    rust = max( rust, step( 0.0, dy ) * ( 1.0 - smoothstep( 0.0, dL, dy ) ) );
  }
  rust *= step( 0.5, dcol );
  albedo = mix( albedo, albedo * toLin( vec3( 0.62, 0.42, 0.3 ) ), rust * 0.75 );
}
if ( det > 96.0 && det < 160.0 ) {
  // livery band lettering: the G.U.L.P. wordmark mid-car, an asset number near the end
  float u = puv.x, v = ( vLocal.y - 0.585 ) / 0.28;
  float ink = 0.0;
  float cw = 0.24, gap = 0.08;
  float wu = ( u + 2.0 ) / ( cw + gap );
  int gi = int( floor( wu ) );
  vec2 cc = vec2( fract( wu ) * ( cw + gap ) / cw, v );
  if ( wu >= 0.0 && gi < 8 ) {
    int gg = gi == 0 ? 10 : gi == 2 ? 11 : gi == 4 ? 12 : gi == 6 ? 13 : 14;
    ink = glyph( gg, cc );
  }
  float nu = ( u - 4.4 ) / ( cw + gap );
  int ni = int( floor( nu ) );
  if ( nu >= 0.0 && ni < 4 ) {
    int num = 4100 + int( vCar ) * 37;
    int d = ni == 0 ? num / 1000 : ni == 1 ? ( num / 100 ) % 10 : ni == 2 ? ( num / 10 ) % 10 : num % 10;
    ink = max( ink, glyph( d, vec2( fract( nu ) * ( cw + gap ) / cw, v ) ) );
  }
  albedo = mix( albedo, toLin( vec3( 0.17, 0.15, 0.12 ) ), ink * mix( 0.35, 1.0, fine ) );
}
vec3 tEmisCol = vec3( 0.0 );
if ( det > 160.0 ) {
  if ( emis > 0.0 ) {
    // individual panes: dark mullions every 0.84 m, a transom every 1.1 m, each pane brightest
    // at its heart and dimming to its frame (a lamp-lit room, not a lit key)
    float pu = fract( puv.x / 0.84 ), pv = fract( vLocal.y / 1.1 );
    float mu = abs( pu - 0.5 ) * 0.84, mt = abs( pv - 0.5 ) * 1.1;
    // panes are separate geometry now (dark frames between them); only a soft lamp falloff here
    emis *= 0.8 + 0.2 * sin( fract( vLocal.y / 2.2 ) * 3.14159 );
  } else {
    float sl = fract( vLocal.y / 0.14 );
    albedo *= mix( 1.0, 0.45 + 0.7 * smoothstep( 0.15, 0.7, sl ), fine );
    tBumpH += smoothstep( 0.15, 0.7, sl ) * 0.4 * fine;
  }
}
if ( emis > 0.0 ) {
  float room = h13( vec3( floor( vLocal.z / 1.05 ), floor( vLocal.y / 1.4 ), carSeed + sign( vLocal.x ) ) );
  // the side-hull window band is the car's brightest, most continuous warm line; roof pods,
  // lanterns and the tower glow second
  bool hullBand = vLocal.y < 10.0;
  float lit = hullBand ? 0.9 + 0.25 * room : ( room < 0.12 ? 0.25 : 0.75 + 0.5 * room );
  float grad = 1.15 - 0.35 * fract( vLocal.y / 1.4 );
  // warm amber lamp light (#FFC070), never white: windows and gauges take the amber, thrusters
  // and furnaces keep their own colour; cold steerage slits stay cool
  bool cold = albedo.b > albedo.r;
  bool pane = det > 160.0 || emis < 0.99;
  vec3 lamp = cold ? albedo * 0.8 : ( pane ? toLin( vec3( 1.0, 0.75, 0.5 ) ) : albedo );
  tEmisCol = mix( lamp, lamp * vec3( 1.0, 0.88, 0.76 ), room ) * ( pane ? ( hullBand ? 2.4 : 1.3 ) : 1.8 ) * lit * grad * ( 0.96 + 0.04 * sin( uTimeT * 7.0 + room * 40.0 ) ) * emis;
  albedo *= 1.0 - emis;
  tMetal *= 1.0 - emis;
}
float camD = length( vViewPosition );
// hold each paint's own hue at range: sub-pixel trim (rivets, scratches, wear) must not
// average the teal plate or red copper into brass
if ( emis <= 0.0 && det > 32.0 && det < 96.0 ) {
  vec3 base = toLin( vColor.rgb );
  float bl = dot( base, vec3( 0.3, 0.59, 0.11 ) );
  base = mix( vec3( bl ), base, tMetal < 0.5 ? 1.0 : 0.85 ) * 0.85;
  albedo = mix( albedo, base, 0.45 * smoothstep( 60.0, 200.0, camD ) );
  diffuseColor.rgb = albedo;
}
tEmisCol *= mix( 1.0, 1.35, smoothstep( 60.0, 320.0, camD ) );
// hold the verdigris / rose-copper body colour through the haze at range
if ( det > 32.0 && det < 96.0 ) tEmisCol += albedo * 0.3 * smoothstep( 90.0, 320.0, camD );
float tAo = mix( 0.32, 1.0, smoothstep( -3.2, 0.8, vLocal.y ) ) * mix( 1.0, bao * bao, 0.85 );
diffuseColor.rgb = albedo;
`;

// replaces <normal_fragment_maps>: baked object-space normal (hd detail), then the procedural bump
const HULL_NORMAL = /* glsl */ `
if ( hasUv ) {
  vec3 nb = texture2D( uNrm, vTUv ).xyz * 2.0 - 1.0;
  vec3 nw = qrotF( vCarQ, normalize( vec3( nb.x, nb.z, -nb.y ) ) );
  vec3 nv = normalize( ( viewMatrix * vec4( nw, 0.0 ) ).xyz );
  float agree = smoothstep( 0.05, 0.45, dot( nv, normal ) );
  normal = normalize( mix( normal, nv, agree ) );
}
normal = bumpArb( - vViewPosition, normal, vec2( dFdx( tBumpH ), dFdy( tBumpH ) ) * 1.6, faceDirection );
`;

const HULL_AO = /* glsl */ `
{
  float rimF = pow( 1.0 - saturate( dot( normal, normalize( vViewPosition ) * -1.0 ) ), 3.0 );
  float upN = dot( normal, normalize( ( viewMatrix * vec4( 0.0, 1.0, 0.0, 0.0 ) ).xyz ) );
  // cool, dim shadow side below; warm sky fill above. No warm rim at range: it gilded the rake.
  vec3 fillUp = mix( vec3( 0.12, 0.18, 0.26 ), vec3( 0.52, 0.42, 0.30 ), smoothstep( -0.7, 0.7, upN ) );
  reflectedLight.indirectDiffuse += diffuseColor.rgb * fillUp * mix( 0.25, 0.65, smoothstep( -0.6, 0.6, upN ) );
  totalEmissiveRadiance += vec3( 1.0, 0.62, 0.32 ) * rimF * 0.06;
  // the belly: warm bounce from its own thrusters on down-facing skin, and a cool sky-fill rim
  float downN = smoothstep( 0.05, -0.75, upN );
  totalEmissiveRadiance += diffuseColor.rgb * vec3( 1.0, 0.6, 0.3 ) * 0.55 * downN * ( 0.85 + 0.15 * sin( uTimeT * 23.0 + vCar ) );
  totalEmissiveRadiance += vec3( 0.62, 0.40, 0.22 ) * rimF * 0.24 * downN;
  // HULL-SIDE FILL: the frame is usually backlit, so the flanks get their own hemisphere light,
  // cool teal sky from above and warm gold ground bounce from below, strongest on vertical
  // faces, keeping the verdigris and copper hues at ~2/3 of the lit tops. Metals take it on
  // their albedo (they have little diffuse); tAo below keeps true recesses near-black.
  float sideN = 1.0 - abs( upN );
  vec3 hemi = mix( vec3( 0.62, 0.48, 0.30 ), vec3( 0.34, 0.50, 0.52 ), smoothstep( -0.8, 0.8, upN ) );
  vec3 fillAlb = mix( diffuseColor.rgb, albedo * ( isBrass ? 1.0 : 0.55 ), tMetal );
  reflectedLight.indirectDiffuse += fillAlb * hemi * ( 0.22 + 0.5 * sideN ) * ( 1.0 - 0.8 * downN );
  // a thin warm rim on silhouette edges that face the sun
  vec3 sunV = normalize( ( viewMatrix * vec4( uSunDirT, 0.0 ) ).xyz );
  float upper = smoothstep( -0.15, 0.45, upN );
  totalEmissiveRadiance += vec3( 1.0, 0.7, 0.4 ) * pow( rimF, 1.5 ) * ( 0.25 + saturate( dot( normal, sunV ) + 0.2 ) ) * 0.42 * upper * ( 1.0 - downN );
  // brass hoops, rails and finials catch a small hot edge glint; copper domes a softer one
  vec3 rv = reflect( -normalize( vViewPosition ), normal );
  vec3 upV = normalize( ( viewMatrix * vec4( 0.0, 1.0, 0.0, 0.0 ) ).xyz );
  float glint = pow( saturate( dot( rv, sunV ) ), 28.0 ) * 2.4 + pow( saturate( dot( rv, upV ) ), 6.0 ) * 0.22;
  if ( isBrass ) totalEmissiveRadiance += vec3( 1.0, 0.8, 0.5 ) * ( 0.03 + 0.45 * pow( rimF, 2.0 ) + glint ) * ( 1.0 - 0.7 * downN );
  else if ( tMetal > 0.5 ) totalEmissiveRadiance += vec3( 1.0, 0.62, 0.48 ) * glint * 0.35 * ( 1.0 - 0.7 * downN );
  else if ( tMetal > 0.5 ) totalEmissiveRadiance += vec3( 1.0, 0.66, 0.5 ) * 0.18 * pow( rimF, 2.0 ) * upper;
}
// art-directed key: keep the sun's intensity but pull its colour most of the way to a soft
// neutral, so teal plate, red copper and gold brass stay three different hues under the
// orange sky (the refs are lit neutral; a fully gold key turns the whole rake to brass)
{
  vec3 dc = max( diffuseColor.rgb, vec3( 1e-3 ) );
  vec3 irr = reflectedLight.directDiffuse / dc;
  float il = dot( irr, vec3( 0.3, 0.59, 0.11 ) );
  reflectedLight.directDiffuse = mix( reflectedLight.directDiffuse, dc * il * vec3( 1.04, 0.98, 0.9 ), 0.72 );
  float sl = dot( reflectedLight.directSpecular, vec3( 0.3, 0.59, 0.11 ) );
  reflectedLight.directSpecular = mix( reflectedLight.directSpecular, vec3( sl ) * vec3( 1.04, 0.98, 0.9 ), 0.25 ) * ( isBrass ? 4.2 : 1.0 );
}
reflectedLight.indirectDiffuse *= tAo;
// painted plate is a dielectric: keep the grazing sky sheen off it so flanks read as paint, not chrome
reflectedLight.indirectSpecular *= tAo * ( 0.3 + 0.7 * tMetal );
reflectedLight.directDiffuse *= mix( 1.0, tAo, 0.92 );
reflectedLight.directSpecular *= mix( 1.0, tAo, 0.92 );
`;

// replaces <fog_fragment>: the shared haze, held back when the train passes close so its darks
// stay dark, and given its full weight only with real distance
const HULL_FOG = /* glsl */ `
{
  float hzd = length( vHazeWorld - cameraPosition );
  float farK = smoothstep( 80.0, 400.0, hzd );
  vec3 cc = gl_FragColor.rgb;
  // paint hue hold: the orange key, sky fill and sub-pixel brass trim all drag the plate and
  // copper toward one gold at range. Keep the lit value, but re-aim its chroma at the paint's
  // own hue (teal-grey plate, red copper), so the three-metal split survives 100-180 m.
  if ( emis <= 0.0 && det > 32.0 && det < 96.0 ) {
    vec3 pb = toLin( vColor.rgb );
    float pl = max( dot( pb, vec3( 0.299, 0.587, 0.114 ) ), 1e-3 );
    float ol = dot( cc, vec3( 0.299, 0.587, 0.114 ) );
    vec3 held = ol * mix( vec3( 1.0 ), pb / pl, 0.6 );
    cc = mix( cc, held, ( vCar > 0.5 ? 0.6 : 0.25 ) * smoothstep( 30.0, 140.0, length( vViewPosition ) ) );
  }
  // the shadow side and belly sink cool and dark (ref: black-teal undersides)
  {
    float sh = 1.0 - smoothstep( 0.0, 0.6, tAo );
    cc = mix( cc, cc * vec3( 0.75, 0.75, 0.73 ), sh );
    // ref A's strong black recesses: cavities, the gaps between cars, under domes, the belly
    if ( emis <= 0.0 ) {
      // value range: cavities, down-facing skin and the low hull fall to the frame's darkest
      // values so windows and thrusters glow against dark mass; the lit tops are untouched
      float cav = 1.0 - smoothstep( 0.15, 0.7, bao );
      float belly = smoothstep( -0.1, -0.75, Nl.y );
      float low = 1.0 - smoothstep( -5.5, 2.0, vLocal.y );
      cc *= mix( 1.0, 0.1, cav ) * mix( 1.0, 0.45, low );
      cc = mix( cc, cc * 0.32 + toLin( vec3( 0.2, 0.3, 0.31 ) ) * 0.22, belly * ( 1.0 - cav ) );
    }
  }
  // night (daynight.ts): the painted fills and warm rims above are art-directed for the golden
  // day; under the moon the hull sinks to a cool dark mass and only its lights carry it
  if ( emis <= 0.0 ) cc = mix( cc, cc * vec3( 0.3, 0.36, 0.52 ), uNight );
  // capped at 0.35 so the teal / copper / brass split survives 100-180 m
  // the scene's distance fog at the train's depth (~0.2 at 140 m: a third under r67; the sun rim separates the top edge)
  gl_FragColor.rgb = mix( cc, applyHaze( cc ), mix( 0.05, 0.28, smoothstep( 40.0, 200.0, hzd ) ) );
}
`;

function makeHullMaterial(uP: THREE.IUniform, uQ: THREE.IUniform, atlas: THREE.Texture, nrm: THREE.Texture, env: THREE.Texture) {
  const m = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.5, metalness: 0.5, envMap: env, envMapIntensity: 0.8 });
  m.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, { uCarP: uP, uCarQ: uQ, uTimeT: HAZE.uTime, uSunDirT: HAZE.uSunDir, uAtlas: { value: atlas }, uNrm: { value: nrm } });
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\n' + HULL_VERT_DECL)
      .replace('#include <beginnormal_vertex>', HULL_BEGINNORMAL)
      .replace('#include <begin_vertex>', HULL_BEGIN);
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\n' + HULL_FRAG_DECL)
      .replace('#include <color_fragment>', HULL_SURFACE)
      .replace('#include <roughnessmap_fragment>', 'float roughnessFactor = tRough;')
      .replace('#include <metalnessmap_fragment>', 'float metalnessFactor = tMetal;')
      .replace('#include <normal_fragment_maps>', HULL_NORMAL)
      .replace('#include <emissivemap_fragment>', 'totalEmissiveRadiance = tEmisCol;')
      .replace('#include <aomap_fragment>', HULL_AO)
      .replace('#include <fog_fragment>', HULL_FOG)
      // extraction colour key: a negative alpha tags the hull in the HDR scene target (nothing else can write
      // one: every blend mode only pulls it back toward 1), so post.ts keeps the train in colour under the grey wash
      .replace('#include <dithering_fragment>', '#include <dithering_fragment>\n  gl_FragColor.a = -1.0;');
  };
  withHaze(m, 0.7);
  m.customProgramCacheKey = () => 'trainHullStd';
  return m;
}

/** A tiny equirect sky for the metals to reflect: gold above, bright haze at the horizon, teal shadow below. */
function makeTrainEnv() {
  const W = 64, H = 32, d = new Uint8Array(W * H * 4);
  const top = new THREE.Color(0xd8d2c4), hor = new THREE.Color(0xc9b89a), bot = new THREE.Color(PAL.shadowTeal), c = new THREE.Color();
  for (let y = 0; y < H; y++) {
    const v = 1 - y / (H - 1); // 1 = up
    if (v > 0.5) c.lerpColors(hor, top, Math.min(1, (v - 0.5) * 2.4)); else c.lerpColors(bot, hor, Math.pow(v * 2, 3));
    for (let x = 0; x < W; x++) {
      const o = (y * W + x) * 4;
      d[o] = Math.round(c.r * 255); d[o + 1] = Math.round(c.g * 255); d[o + 2] = Math.round(c.b * 255); d[o + 3] = 255;
    }
  }
  const t = new THREE.DataTexture(d, W, H, THREE.RGBAFormat);
  t.mapping = THREE.EquirectangularReflectionMapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.magFilter = THREE.LinearFilter; t.minFilter = THREE.LinearFilter;
  t.needsUpdate = true;
  return t;
}
// Additive cones: thruster flames (kind 0) and sweeping searchlights (kind 2).
const CONE_VERT = /* glsl */ `
${POSE_GLSL}
attribute float aCar;
attribute vec3 aOrigin;
attribute vec4 aFx; // along 0..1, kind, seed, length
uniform float uTimeT;
varying float vAlong;
varying float vKind;
varying float vFres;
varying float vDist;
vec3 rotY( vec3 v, float a ) { float c = cos( a ), s = sin( a ); return vec3( c * v.x + s * v.z, v.y, -s * v.x + c * v.z ); }
vec3 rotX( vec3 v, float a ) { float c = cos( a ), s = sin( a ); return vec3( v.x, c * v.y - s * v.z, s * v.y + c * v.z ); }
void main() {
  int ci = int( aCar + 0.5 );
  vec4 q = uCarQ[ ci ];
  vec3 loc;
  vec3 nl = normal;
  if ( aFx.y > 1.5 ) {
    // searchlight: cone space (axis -y), swept and tilted outboard
    float t = uTimeT * 0.35 + aFx.z * 6.0;
    float yaw = sin( t ) * 1.1 + aFx.z * 3.14;
    float tilt = 0.55 + 0.25 * sin( t * 1.7 + 1.0 );
    loc = aOrigin + rotY( rotX( position, tilt ), yaw );
    nl = rotY( rotX( normal, tilt ), yaw );
  } else {
    float fl = 0.72 + 0.2 * sin( uTimeT * 37.0 + aFx.z * 17.0 ) * sin( uTimeT * 23.0 + aFx.z * 5.0 ) + 0.08 * sin( uTimeT * 61.0 + aFx.z * 29.0 );
    loc = aOrigin + ( position - aOrigin ) * vec3( 1.0, 1.0, 1.0 ) * mix( 1.0, fl * ( 0.62 + 0.76 * fract( aFx.z * 3.7 ) ), aFx.x );
  }
  vec3 w = qrot( q, loc ) + uCarP[ ci ].xyz;
  vec3 nw = qrot( q, nl );
  vec4 mv = modelViewMatrix * vec4( w, 1.0 );
  gl_Position = projectionMatrix * mv;
  vec3 V = normalize( cameraPosition - ( modelMatrix * vec4( w, 1.0 ) ).xyz );
  vFres = abs( dot( normalize( mat3( modelMatrix ) * nw ), V ) );
  vAlong = aFx.x;
  vKind = aFx.y;
  vDist = -mv.z;
}
`;
const CONE_FRAG = /* glsl */ `
uniform vec3 uCore;
uniform vec3 uRing;
uniform vec3 uSearch;
uniform float uHazeDensity;
varying float vAlong;
varying float vKind;
varying float vFres;
varying float vDist;
void main() {
  float fade = exp( -pow( vDist * uHazeDensity * 0.8, 1.45 ) );
  vec3 c;
  if ( vKind > 1.5 ) {
    float a = pow( clamp( 1.0 - vAlong, 0.0, 1.0 ), 1.6 ) * smoothstep( 0.0, 0.6, vFres ) * 0.16;
    c = uSearch * a;
  } else {
    float a = pow( clamp( 1.0 - vAlong, 0.0, 1.0 ), 2.2 ) * smoothstep( 0.05, 0.8, vFres );
    c = mix( uCore, uRing, smoothstep( 0.0, 0.45, vAlong ) ) * a * 1.6;
  }
  gl_FragColor = vec4( c * fade, 1.0 );
}
`;

// Camera-facing glow sprites: thruster nozzles, headlamp eyes, searchlight lenses.
const GLOW_VERT = /* glsl */ `
${POSE_GLSL}
attribute float aCar;
attribute vec2 aCorner;
attribute vec4 aGlow; // size, kind, seed, -
uniform float uTimeT;
varying vec2 vC;
varying float vKind;
varying float vDist;
varying float vFl;
void main() {
  int ci = int( aCar + 0.5 );
  vec3 w = qrot( uCarQ[ ci ], position ) + uCarP[ ci ].xyz;
  vec4 mv = modelViewMatrix * vec4( w, 1.0 );
  float fl = 0.78 + 0.14 * sin( uTimeT * 41.0 + aGlow.z * 13.0 ) + 0.08 * sin( uTimeT * 67.0 + aGlow.z * 31.0 );
  // sprites keep a minimum screen size so the thrusters still sparkle at 600 m
  float sz = aGlow.x * fl * max( 1.0, -mv.z / 220.0 );
  mv.xy += aCorner * sz;
  mv.z += sz * 0.5; // pull toward the camera so the hull doesn't clip the halo
  gl_Position = projectionMatrix * mv;
  vC = aCorner;
  vKind = aGlow.y;
  vDist = -mv.z;
  vFl = fl;
}
`;
const GLOW_FRAG = /* glsl */ `
uniform vec3 uCore;
uniform vec3 uRing;
uniform vec3 uLamp;
uniform float uHazeDensity;
varying vec2 vC;
varying float vKind;
varying float vDist;
varying float vFl;
void main() {
  float r = length( vC );
  if ( r > 1.0 ) discard;
  float core = exp( -r * r * 22.0 );
  float halo = exp( -r * r * 4.0 ) * 0.55;
  float rays = ( exp( -abs( vC.y ) * 28.0 ) * 0.35 ) * ( 1.0 - r ); // printed lens streak
  vec3 ring = vKind > 0.5 ? uLamp : uRing;
  vec3 c = uCore * core * 1.4 + ring * ( halo + rays ) * vFl;
  float fade = exp( -pow( vDist * uHazeDensity * 0.7, 1.45 ) ) * 0.85 + 0.15;
  gl_FragColor = vec4( c * fade, 1.0 );
}
`;

// Steam puffs: soft billboards, CPU-simulated ring buffer, normal blend + haze.
const STEAM_VERT = /* glsl */ `
#include <common>
attribute vec2 aCorner;
attribute vec4 aPuff;  // x y z size
attribute vec2 aPuff2; // alpha, seed
varying vec2 vC;
varying float vA;
varying float vSeed;
varying vec3 vNw;
void main() {
  vec3 right = vec3( viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0] );
  vec3 up = vec3( viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1] );
  float a = aPuff2.y * 6.283;
  vec2 cr = vec2( cos( a ) * aCorner.x - sin( a ) * aCorner.y, sin( a ) * aCorner.x + cos( a ) * aCorner.y );
  vec3 transformed = aPuff.xyz + ( right * cr.x + up * cr.y ) * aPuff.w;
  vC = aCorner;
  vA = aPuff2.x;
  vSeed = aPuff2.y;
  #include <project_vertex>
}
`;
const STEAM_FRAG = /* glsl */ `
#include <common>
${NOISE_GLSL}
uniform vec3 uSteamLit;
uniform vec3 uSteamShade;
varying vec2 vC;
varying float vA;
varying float vSeed;
void main() {
  float r = length( vC );
  float n = fbm3( vec3( vC * 2.2, vSeed * 17.0 ) );
  float a = smoothstep( 1.0, 0.25, r + ( n - 0.5 ) * 0.7 ) * vA;
  if ( a < 0.01 ) discard;
  // lit from the upper side, teal-shaded beneath: a printed cloud, not a grey blob
  vec3 c = mix( uSteamShade, uSteamLit, smoothstep( -0.6, 0.7, vC.y + ( n - 0.5 ) * 0.8 ) );
  gl_FragColor = vec4( c, a );
  #include <fog_fragment>
}
`;

// ------------------------------------------------------------------ helpers
function lin(hex: number, k = 1) { return new THREE.Color(hex).multiplyScalar(k); }

interface Built { geo: THREE.BufferGeometry; start: Int32Array; end: Int32Array }

/** One car type's authored mesh, flattened (game frame, already scaled). */
interface TypeSrc {
  pos: Float32Array; nrm: Float32Array; uv: Float32Array; col: Uint8Array; mat: Uint8Array; idx: Uint32Array;
  fx: FxOut;
}

function hexBytes(h: number) { return [(h >> 16) & 255, (h >> 8) & 255, h & 255]; }

/** Flatten a GLTF node (group of per-material primitives + emitter empties) into a TypeSrc. */
function readType(root: THREE.Object3D, type: CarType, withFx: boolean): TypeSrc {
  const parts: { g: THREE.BufferGeometry; paint: string }[] = [];
  const fx: FxOut = { emitters: [], vents: [] };
  const sc = TRAIN_SCALE, szk = TRAIN_SCALE * (type === 'loco' ? 1.25 : 1);
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) { parts.push({ g: m.geometry as THREE.BufferGeometry, paint: (m.material as THREE.Material).name }); return; }
    const tag = o.name.split('~')[1];
    if (!tag || !withFx) return;
    const f = tag.split('-');
    const x = o.position.x * sc, y = o.position.y * sc, z = o.position.z * sc;
    if (f[0] === 'VT') fx.vents.push({ car: 0, x, y, z, rate: Number(f[1]) / 100, size: (Number(f[2]) / 100) * szk });
    else if (f[0] === 'EM') {
      const kind = f[1] === 'thr' ? 0 : f[1] === 'lamp' ? 1 : 2;
      const dir = f[2];
      const size = kind === 2 ? 1 : (Number(f[3]) / 100) * szk;
      fx.emitters.push({ car: 0, x, y, z, dx: 0, dy: dir === 'down' || kind === 2 ? -1 : 0, dz: dir === 'back' ? -1 : dir === 'fwd' ? 1 : 0, size, kind });
    }
  });
  let nv = 0, ni = 0;
  for (const { g } of parts) { nv += g.attributes.position.count; ni += g.index ? g.index.count : g.attributes.position.count; }
  const out: TypeSrc = {
    pos: new Float32Array(nv * 3), nrm: new Float32Array(nv * 3), uv: new Float32Array(nv * 2).fill(-1),
    col: new Uint8Array(nv * 3), mat: new Uint8Array(nv * 4), idx: new Uint32Array(ni), fx,
  };
  let vo = 0, io = 0;
  for (const { g, paint } of parts) {
    const P = g.attributes.position, N = g.attributes.normal, U = g.attributes.uv;
    const pt = PAINTS[paint] ?? PAINTS.plate;
    const rgb = hexBytes(pt.c);
    const mb = [Math.round((pt.e ?? 0) * 255), Math.round((pt.m ?? 0.5) * 15) * 16 + Math.round((pt.r ?? 0.5) * 15), Math.round((pt.p ?? 0) * 255), pt.d ?? 0];
    for (let i = 0; i < P.count; i++) {
      const o = vo + i;
      out.pos[o * 3] = P.getX(i) * sc; out.pos[o * 3 + 1] = P.getY(i) * sc; out.pos[o * 3 + 2] = P.getZ(i) * sc;
      out.nrm[o * 3] = N.getX(i); out.nrm[o * 3 + 1] = N.getY(i); out.nrm[o * 3 + 2] = N.getZ(i);
      if (U) { out.uv[o * 2] = U.getX(i); out.uv[o * 2 + 1] = U.getY(i); }
      out.col[o * 3] = rgb[0]; out.col[o * 3 + 1] = rgb[1]; out.col[o * 3 + 2] = rgb[2];
      out.mat.set(mb, o * 4);
    }
    if (g.index) for (let i = 0; i < g.index.count; i++) out.idx[io + i] = g.index.getX(i) + vo;
    else for (let i = 0; i < P.count; i++) out.idx[io + i] = i + vo;
    io += g.index ? g.index.count : P.count;
    vo += P.count;
  }
  return out;
}

function buildConsist(types: CarType[], src: Map<CarType, TypeSrc>, fx: FxOut | null): Built {
  let nv = 0, ni = 0;
  for (const t of types) { const k = src.get(t)!; nv += k.pos.length / 3; ni += k.idx.length; }
  const pos = new Float32Array(nv * 3), nrm = new Int8Array(nv * 3), uv = new Float32Array(nv * 2), col = new Uint8Array(nv * 3);
  const mat = new Uint8Array(nv * 4), car = new Uint8Array(nv);
  const idx = nv > 65535 ? new Uint32Array(ni) : new Uint16Array(ni);
  const start = new Int32Array(types.length), end = new Int32Array(types.length);
  let vo = 0, io = 0;
  types.forEach((t, slot) => {
    const k = src.get(t)!;
    const n = k.pos.length / 3;
    pos.set(k.pos, vo * 3);
    for (let i = 0; i < n * 3; i++) nrm[vo * 3 + i] = Math.round(Math.max(-1, Math.min(1, k.nrm[i])) * 127);
    uv.set(k.uv, vo * 2);
    col.set(k.col, vo * 3);
    mat.set(k.mat, vo * 4);
    car.fill(slot, vo, vo + n);
    start[slot] = io;
    for (let i = 0; i < k.idx.length; i++) idx[io + i] = k.idx[i] + vo;
    io += k.idx.length;
    end[slot] = io;
    vo += n;
    if (fx) {
      for (const e of k.fx.emitters) fx.emitters.push({ ...e, car: slot });
      for (const v of k.fx.vents) fx.vents.push({ ...v, car: slot });
    }
  });
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(nrm, 3, true));
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3, true));
  geo.setAttribute('aMat', new THREE.BufferAttribute(mat, 4, true));
  geo.setAttribute('aCar', new THREE.BufferAttribute(car, 1, false));
  geo.setIndex(new THREE.BufferAttribute(idx, 1));
  return { geo, start, end };
}

/** Thruster cones (baked car-local) + searchlight cones (cone space) in one geometry. */
function buildCones(emitters: Emitter[], segs: number) {
  const pos: number[] = [], nrm: number[] = [], org: number[] = [], fxa: number[] = [], car: number[] = [], idx: number[] = [];
  const up = new THREE.Vector3(0, 1, 0), d = new THREE.Vector3(), q = new THREE.Quaternion(), v = new THREE.Vector3(), n = new THREE.Vector3();
  emitters.forEach((e, ei) => {
    if (e.kind === 1) return;
    const search = e.kind === 2;
    const len = search ? 70 : e.size * 3.0;
    const r0 = search ? 0.5 : e.size * 0.42, r1 = search ? 11 : e.size * 0.12;
    d.set(e.dx, e.dy, e.dz).normalize();
    if (search) q.identity(); else q.setFromUnitVectors(up.set(0, -1, 0), d);
    const base = pos.length / 3;
    const rings = 5;
    for (let i = 0; i <= rings; i++) {
      const t = i / rings;
      const r = r0 + (r1 - r0) * Math.pow(t, search ? 1 : 0.7);
      for (let j = 0; j < segs; j++) {
        const a = (j / segs) * Math.PI * 2;
        v.set(Math.cos(a) * r, -t * len, Math.sin(a) * r);
        n.set(Math.cos(a), (r0 - r1) / len, Math.sin(a)).normalize();
        v.applyQuaternion(q); n.applyQuaternion(q);
        if (!search) v.add(new THREE.Vector3(e.x, e.y, e.z));
        pos.push(v.x, v.y, v.z); nrm.push(n.x, n.y, n.z);
        org.push(e.x, e.y, e.z);
        fxa.push(t, e.kind, (ei * 0.6180339) % 1, len);
        car.push(e.car);
      }
    }
    for (let i = 0; i < rings; i++) for (let j = 0; j < segs; j++) {
      const a = base + i * segs + j, b = base + i * segs + ((j + 1) % segs), c = base + (i + 1) * segs + ((j + 1) % segs), dd = base + (i + 1) * segs + j;
      idx.push(a, b, c, a, c, dd);
    }
  });
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute('aOrigin', new THREE.Float32BufferAttribute(org, 3));
  g.setAttribute('aFx', new THREE.Float32BufferAttribute(fxa, 4));
  g.setAttribute('aCar', new THREE.Float32BufferAttribute(car, 1));
  g.setIndex(idx);
  return g;
}

function buildGlows(emitters: Emitter[]) {
  const pos: number[] = [], cor: number[] = [], gl: number[] = [], car: number[] = [], idx: number[] = [];
  emitters.forEach((e, ei) => {
    const b = pos.length / 3;
    const size = e.kind === 0 ? e.size * 2.0 : e.kind === 1 ? e.size * 2.2 : e.size * 2.6;
    for (const [cx, cy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
      pos.push(e.x, e.y, e.z); cor.push(cx, cy); gl.push(size, e.kind, (ei * 0.381966) % 1, 0); car.push(e.car);
    }
    idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
  });
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('aCorner', new THREE.Float32BufferAttribute(cor, 2));
  g.setAttribute('aGlow', new THREE.Float32BufferAttribute(gl, 4));
  g.setAttribute('aCar', new THREE.Float32BufferAttribute(car, 1));
  g.setIndex(idx);
  return g;
}

// ------------------------------------------------------------------ the train
export class Train {
  readonly group = new THREE.Group();
  /** max angle between neighbouring cars this frame (radians), for the lab guardrail */
  maxKink = 0;
  private ctx: ViewCtx;
  private path: TrainPath;
  private types: CarType[];
  private lens: number[];
  private nCars: number;
  private sTab: Float32Array;
  private period: number;
  private tOrigin: number | null = null;
  private hiMesh: THREE.Mesh | null = null;
  private loA: THREE.Mesh | null = null;
  private loB: THREE.Mesh | null = null;
  private hi: Built | null = null;
  private lo: Built | null = null;
  private maxHi: number;
  /** resolves once the authored cars (train.glb + atlas) are in */
  readonly ready: Promise<void>;
  /** the cars have arrived and been built */
  loaded = false;
  private hiDist: number;
  private uP: { value: Float32Array };
  private uQ: { value: Float32Array };
  private rollK: Float32Array; // smoothed lateral acceleration per car (x,y,z)
  private vents: Vent[] = [];
  private ventAcc = new Float32Array(0);
  // steam ring buffer
  private steam: THREE.Mesh | null = null;
  private puffN: number;
  private puffPos: Float32Array;
  private puffVel: Float32Array;
  private puffAge: Float32Array;
  private puffLife: Float32Array;
  private puffSize: Float32Array;
  private aPuff: THREE.InstancedBufferAttribute | null = null;
  private aPuff2: THREE.InstancedBufferAttribute | null = null;
  private puffHead = 0;
  private rng = 1;
  private first = true;
  private disposables: { dispose(): void }[] = [];
  // scratch
  private a = new THREE.Vector3();
  private b = new THREE.Vector3();
  private ta = new THREE.Vector3();
  private tb = new THREE.Vector3();
  private f = new THREE.Vector3();
  private x = new THREE.Vector3();
  private y = new THREE.Vector3();
  private k = new THREE.Vector3();
  private prevF = new THREE.Vector3();
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private v = new THREE.Vector3();
  /** extraction mode (render/run/extract.ts): a short rake holding a straight dock pose */
  private dock: { x: number; y: number; z: number; fx: number; fz: number; arrive: number } | null = null;
  constructor(seed: number, ctx: ViewCtx, consist?: CarType[]) {
    this.ctx = ctx;
    const q = Math.max(0, Math.min(3, ctx.quality | 0));
    this.types = consist ? consist.slice() : q >= 2 ? CONSIST.slice() : [...CONSIST.slice(0, 8), 'steerage', 'dorm', 'freight', 'tail'];
    this.nCars = this.types.length;
    this.lens = this.types.map((t) => CAR_LEN[t]);
    // the near pass crosses the view a player gets on arrival (spawn looking at the arena), and
    // the loco reaches it ~14 s into the run, then again every lap
    const w = ctx.world, ar = w.arena, sp = w.playerSpawn;
    const rot = Math.atan2(ar.z - sp.z, ar.x - sp.x) + (((seed % 7) - 3) * 0.06);
    // close pass: ~45 m beyond the arena centre, ~36 m over its floor, so from the arrival
    // spot it crosses the frame at ~100 m and fills it for a few seconds
    this.path = new TrainPath(seed, rot, NEAR_D, w.terrain(ar.x, ar.z) + NEAR_Y, PATH_K);
    {
      // integrate the head's arc position over one lap (construction-time only)
      const L = this.path.length;
      const trainLen = this.lens.reduce((a, b) => a + b, 0);
      const speed = (sh: number) => {
        let x = ((sh % L) + L) % L; if (x > L / 2) x -= L;
        const e = x < -SLOW_BEFORE ? -SLOW_BEFORE - x : x > trainLen + SLOW_AFTER ? x - trainLen - SLOW_AFTER : 0;
        const k = Math.min(1, e / RAMP), w = 1 - k * k * (3 - 2 * k);
        return V_FAR + (V_NEAR - V_FAR) * w;
      };
      // integrate from the near point to where the head stands at t = 0 (T_FIRST < 0: past it)
      let s0 = 0;
      for (let t = 0; t < Math.abs(T_FIRST); t += TAB_DT) s0 += (T_FIRST < 0 ? 1 : -1) * speed(s0) * TAB_DT;
      const tab: number[] = [s0];
      let sh = s0;
      while (sh < s0 + L) { sh += speed(sh) * TAB_DT; tab.push(sh); }
      // scale the last step so the table closes exactly on one lap
      tab[tab.length - 1] = s0 + L;
      this.sTab = new Float32Array(tab);
      this.period = (tab.length - 1) * TAB_DT;
    }
    this.hiDist = q >= 3 ? 560 : 440;
    this.uP = { value: new Float32Array(MAX_CARS * 4) };
    this.uQ = { value: new Float32Array(MAX_CARS * 4) };
    for (let i = 0; i < MAX_CARS; i++) this.uQ.value[i * 4 + 3] = 1;
    this.rollK = new Float32Array(MAX_CARS * 3);

    // --- hull material (one for all LODs): lit PBR metal + baked atlases + shared haze
    const base = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
    // (shared across islands, like the env: a new env texture was a new PMREM bake every warp)
    const setup = (t: THREE.Texture) => { t.flipY = false; t.colorSpace = THREE.NoColorSpace; t.anisotropy = 4; };
    const atlas = sharedTexture(base + 'assets/train/train_atlas.jpg', 'train', setup);
    const nrmTex = sharedTexture(base + 'assets/train/train_nrm.jpg', 'train', setup);
    const env = sharedMade('trainEnv', makeTrainEnv);
    const hullMat = makeHullMaterial(this.uP, this.uQ, atlas, nrmTex, env);
    this.disposables.push(hullMat);

    this.maxHi = 8;
    // a joint steam vent at every coupling (the cars' own vents arrive with the meshes)
    for (let i = 0; i < this.nCars - 1; i++) this.vents.push({ car: i, x: 0, y: -0.6, z: -this.lens[i] / 2, rate: 0.28, size: 0.7 });
    this.ventAcc = new Float32Array(this.vents.length);

    // decoded once a session; each island's rake is built from it in frame-budget slices
    // (client/render/warm.ts), so it lands while the posse is fighting without a hitch
    trainGlb ??= new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).loadAsync(base + 'assets/train/train.glb').then((g) => g.scene);
    const pause = function* (at: string) { budget.note = at; yield; };
    this.ready = trainGlb.then((src) => new Promise<void>((done) => budget.add(function* (this: Train): Generator<unknown, void, unknown> {
      // the rake's meshes depend only on its cars, not the island: built once a session per
      // consist, every island's train draws the same GPU buffers (its own draw ranges)
      const key = `${q >= 2 ? 'hi' : 'lo'}|${this.types.join(',')}`;
      let cars = carsCache.get(key);
      if (!cars) {
        const hiSrc = new Map<CarType, TypeSrc>(), loSrc = new Map<CarType, TypeSrc>();
        const uniq = Array.from(new Set(this.types));
        for (const t of uniq) {
          yield* pause('train:read');
          const h = src.getObjectByName('hi_' + t), l = src.getObjectByName('lo_' + t);
          if (!h || !l) throw new Error('train.glb missing ' + t);
          hiSrc.set(t, readType(h, t, true));
          loSrc.set(t, readType(l, t, false));
        }
        const fxOut: FxOut = { emitters: [], vents: [] };
        yield* pause('train:lo');
        const lo = buildConsist(this.types, loSrc, null);
        yield* pause('train:hi');
        const hi = q >= 2 ? buildConsist(this.types, hiSrc, fxOut) : null;
        if (!hi) buildConsist(this.types, hiSrc, fxOut).geo.dispose(); // emitters only
        cars = { lo, hi, fx: fxOut };
        carsCache.set(key, cars);
        yield* pause('train:fx');
      }
      const fx = cars.fx;
      this.lo = viewOf(cars.lo);
      if (cars.hi) this.hi = viewOf(cars.hi);
      for (const v of fx.vents) this.vents.push(v);
      this.ventAcc = new Float32Array(this.vents.length);

      const mk = (geo: THREE.BufferGeometry, name: string) => {
        const m = new THREE.Mesh(geo, hullMat);
        m.frustumCulled = false; m.name = name; m.matrixAutoUpdate = false;
        this.group.add(m);
        return m;
      };
      // two lo-LOD views of the same buffers (cars before / after the near span)
      const loB = new THREE.BufferGeometry();
      for (const k of Object.keys(this.lo.geo.attributes)) loB.setAttribute(k, this.lo.geo.attributes[k]);
      loB.setIndex(this.lo.geo.index);
      this.loA = mk(this.lo.geo, 'trainLoA');
      this.loB = mk(loB, 'trainLoB');
      // (not disposed: disposing a view deletes the shared buffers under every island's train)
      if (this.hi) this.hiMesh = mk(this.hi.geo, 'trainHi');

      // --- cones + glows
      const core = lin(PAL.thrusterCore, 1.3), ring = lin(PAL.thrusterRing, 1.0);
      const coneGeo = buildCones(fx.emitters, q >= 2 ? 10 : 6);
      const coneMat = new THREE.ShaderMaterial({
        vertexShader: CONE_VERT, fragmentShader: CONE_FRAG, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
        uniforms: { uCarP: this.uP, uCarQ: this.uQ, uTimeT: HAZE.uTime, uCore: { value: core }, uRing: { value: ring }, uSearch: { value: lin(0xfff0c8, 1) }, uHazeDensity: HAZE.uHazeDensity },
      });
      const cones = new THREE.Mesh(coneGeo, coneMat);
      cones.frustumCulled = false; cones.renderOrder = 2; cones.name = 'trainCones';
      this.group.add(cones);
      const glowGeo = buildGlows(fx.emitters);
      const glowMat = new THREE.ShaderMaterial({
        vertexShader: GLOW_VERT, fragmentShader: GLOW_FRAG, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
        uniforms: { uCarP: this.uP, uCarQ: this.uQ, uTimeT: HAZE.uTime, uCore: { value: core }, uRing: { value: ring }, uLamp: { value: lin(PAL.windowGlow, 1.1) }, uHazeDensity: HAZE.uHazeDensity },
      });
      const glows = new THREE.Mesh(glowGeo, glowMat);
      glows.frustumCulled = false; glows.renderOrder = 3; glows.name = 'trainGlows';
      this.group.add(glows);
      this.disposables.push(coneGeo, coneMat, glowGeo, glowMat);
      done();
    }.call(this)))).then(() => { this.loaded = true; });

    // --- steam
    this.puffN = q >= 3 ? 220 : q >= 2 ? 160 : 72;
    this.puffPos = new Float32Array(this.puffN * 3);
    this.puffVel = new Float32Array(this.puffN * 3);
    this.puffAge = new Float32Array(this.puffN).fill(1e9);
    this.puffLife = new Float32Array(this.puffN).fill(1);
    this.puffSize = new Float32Array(this.puffN);
    const sg = new THREE.InstancedBufferGeometry();
    sg.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], 3));
    sg.setAttribute('aCorner', new THREE.Float32BufferAttribute([-1, -1, 1, -1, 1, 1, -1, 1], 2));
    sg.setIndex([0, 1, 2, 0, 2, 3]);
    this.aPuff = new THREE.InstancedBufferAttribute(new Float32Array(this.puffN * 4), 4);
    this.aPuff2 = new THREE.InstancedBufferAttribute(new Float32Array(this.puffN * 2), 2);
    this.aPuff.setUsage(THREE.DynamicDrawUsage); this.aPuff2.setUsage(THREE.DynamicDrawUsage);
    sg.setAttribute('aPuff', this.aPuff); sg.setAttribute('aPuff2', this.aPuff2);
    sg.instanceCount = this.puffN;
    const steamMat = new THREE.ShaderMaterial({
      vertexShader: STEAM_VERT, fragmentShader: STEAM_FRAG, transparent: true, depthWrite: false,
      uniforms: { uSteamLit: { value: lin(0xfff4e0, 1.0) }, uSteamShade: { value: lin(0x9fb5ac, 0.75) } },
    });
    withHaze(steamMat, 0.95);
    steamMat.customProgramCacheKey = () => 'trainSteam';
    this.steam = new THREE.Mesh(sg, steamMat);
    this.steam.frustumCulled = false; this.steam.renderOrder = 4; this.steam.name = 'trainSteam';
    this.group.add(this.steam);
    this.disposables.push(sg, steamMat);

    this.update(0, 0);
  }

  private rand() { this.rng = (this.rng * 16807) % 2147483647; return (this.rng - 1) / 2147483646; }

  /** Head arc-length at time t; exposed for the lab. */
  headAt(time: number) {
    const T = this.sTab, P = this.period, L = this.path.length;
    const n = Math.floor(time / P);
    const f = (time - n * P) / TAB_DT;
    const i = Math.min(T.length - 2, Math.floor(f));
    return T[i] + (T[i + 1] - T[i]) * (f - i) + n * L;
  }
  /** Seconds between near passes (lab / stats). */
  get lapSeconds() { return this.period; }
  /** World position of a car's centre (lab framing). */
  carCentre(i: number, out: THREE.Vector3) { const P = this.uP.value; return out.set(P[i * 4], P[i * 4 + 1], P[i * 4 + 2]); }
  get carCount() { return this.nCars; }

  /**
   * Hold the rake on a straight dock line instead of the sky loop. (x, y, z) is the centre of car
   * `doorCar`'s floor-level side door line; (fx, fz) the heading. arrive 0..1 flies it in from far
   * behind and above (eased), 1 = docked, > 1 = pulling away up and ahead.
   */
  setDock(x: number, y: number, z: number, fx: number, fz: number, arrive: number) {
    this.dock = { x, y, z, fx, fz, arrive };
  }
  /** car slot → its length (for placing the door car) */
  carLength(i: number) { return this.lens[i]; }

  private poseDock(dt: number, time: number) {
    const d = this.dock!, P = this.uP.value, Q = this.uQ.value;
    const a = d.arrive;
    // in: decelerate from 520 m back and 140 m up; out: accelerate away ahead and up
    const k = a <= 1 ? 1 - Math.pow(1 - Math.max(0, a), 3) : 1;
    const out = Math.max(0, a - 1);
    const back = (1 - k) * 520 - out * out * 600, up = (1 - k) * (1 - k) * 140 + out * out * 160;
    const hl = Math.hypot(d.fx, d.fz) || 1, fx = d.fx / hl, fz = d.fz / hl;
    // nose up a touch on approach and departure
    const pitch = (1 - k) * 0.22 + out * 0.25;
    this.f.set(fx * Math.cos(pitch), Math.sin(pitch) * (a <= 1 ? -1 : 1), fz * Math.cos(pitch)).normalize();
    this.y.set(0, 1, 0);
    this.x.crossVectors(this.y, this.f).normalize();
    this.y.crossVectors(this.f, this.x);
    // a slow hover heave while docked
    const heave = 0.35 * Math.sin(time * 0.9);
    let sFront = 0;
    for (let i = 0; i < this.nCars; i++) {
      const L = this.lens[i];
      const along = sFront - L / 2 - back;
      const rk = 0.012 * Math.sin(time * 0.9 + i * 1.7);
      const cr = Math.cos(rk), sr = Math.sin(rk);
      this.ta.copy(this.x).multiplyScalar(cr).addScaledVector(this.y, sr);
      this.tb.copy(this.y).multiplyScalar(cr).addScaledVector(this.x, -sr);
      this.m.makeBasis(this.ta, this.tb, this.f);
      this.q.setFromRotationMatrix(this.m);
      const o = i * 4;
      P[o] = d.x + this.f.x * along; P[o + 1] = d.y + up + heave + this.f.y * along; P[o + 2] = d.z + this.f.z * along;
      Q[o] = this.q.x; Q[o + 1] = this.q.y; Q[o + 2] = this.q.z; Q[o + 3] = this.q.w;
      sFront -= L;
    }
    this.applyLod(0, this.nCars - 1);
    this.updateSteam(dt);
  }

  update(dt: number, time: number) {
    if (this.dock) { this.poseDock(dt, time); return; }
    const P = this.uP.value, Q = this.uQ.value;
    // the train's clock starts when the world appears in the game (first real frame), so the
    // rake is crossing the arrival view at spawn however long the connect/lobby took; the lab poses at
    // absolute t (it only ever calls with dt = 0)
    if (this.tOrigin === null && dt > 0) this.tOrigin = time;
    let sFront = this.headAt(time - (this.tOrigin ?? 0));
    const lag = this.first ? 1 : 1 - Math.exp(-Math.max(0, dt) * 1.6);
    this.first = false;
    this.maxKink = 0;
    const cam = this.ctx.camera.position;
    let nearA = -1, nearB = -1;
    for (let i = 0; i < this.nCars; i++) {
      const L = this.lens[i];
      const sBack = sFront - L;
      // the long locomotive steers on its rear 30 m (its trailing bogies), so the
      // loco/tender joint stays inside the kink guardrail; its nose rides the tangent
      const chord = Math.min(L, 36);
      this.path.at(sBack + chord, this.a);
      this.path.at(sBack, this.b);
      // a slow heave travelling down the rake (long wavelength: adds <0.15° of kink)
      this.a.y += 0.5 * Math.sin(time * 0.75 - (sBack + chord) * 0.015);
      this.b.y += 0.5 * Math.sin(time * 0.75 - sBack * 0.015);
      this.f.subVectors(this.a, this.b).normalize();
      if (chord < L) this.a.copy(this.b).addScaledVector(this.f, L);
      if (i > 0) this.maxKink = Math.max(this.maxKink, Math.acos(Math.min(1, this.f.dot(this.prevF))));
      this.prevF.copy(this.f);
      // lateral acceleration (curvature) across the car → bank into the turn
      this.path.at(sFront + 6, this.ta).sub(this.a);
      this.path.at(sBack - 6, this.tb);
      this.tb.subVectors(this.b, this.tb);
      this.k.subVectors(this.ta.normalize(), this.tb.normalize()).multiplyScalar(1 / (L + 6));
      const ko = i * 3;
      this.rollK[ko] += (this.k.x - this.rollK[ko]) * lag;
      this.rollK[ko + 1] += (this.k.y - this.rollK[ko + 1]) * lag;
      this.rollK[ko + 2] += (this.k.z - this.rollK[ko + 2]) * lag;
      // banked up: world up tilted toward the turn centre, capped ~20°
      this.y.set(this.rollK[ko] * 420, 1, this.rollK[ko + 2] * 420);
      const tilt = Math.hypot(this.y.x, this.y.z);
      if (tilt > 0.36) { this.y.x *= 0.36 / tilt; this.y.z *= 0.36 / tilt; }
      this.x.crossVectors(this.y, this.f).normalize();
      this.y.crossVectors(this.f, this.x);
      // each car rocks on its own phase (±1.5°) about its long axis; roll never changes heading
      const rk = 0.026 * Math.sin(time * 0.9 + i * 1.7) + 0.008 * Math.sin(time * 2.3 + i * 0.6);
      const cr = Math.cos(rk), sr = Math.sin(rk);
      this.ta.copy(this.x).multiplyScalar(cr).addScaledVector(this.y, sr);
      this.y.multiplyScalar(cr).addScaledVector(this.x, -sr);
      this.x.copy(this.ta);
      this.m.makeBasis(this.x, this.y, this.f);
      this.q.setFromRotationMatrix(this.m);
      const o = i * 4;
      P[o] = (this.a.x + this.b.x) * 0.5; P[o + 1] = (this.a.y + this.b.y) * 0.5; P[o + 2] = (this.a.z + this.b.z) * 0.5;
      Q[o] = this.q.x; Q[o + 1] = this.q.y; Q[o + 2] = this.q.z; Q[o + 3] = this.q.w;
      // LOD span
      const dx = P[o] - cam.x, dy = P[o + 1] - cam.y, dz = P[o + 2] - cam.z;
      if (dx * dx + dy * dy + dz * dz < this.hiDist * this.hiDist) { if (nearA < 0) nearA = i; nearB = i; }
      sFront = sBack;
    }
    this.applyLod(nearA, nearB);
    this.updateSteam(dt);
  }

  private applyLod(a: number, b: number) {
    const lo = this.lo;
    if (!lo || !this.loA || !this.loB) return;
    const n = this.nCars;
    if (a >= 0 && b - a + 1 > this.maxHi) {
      // keep the hi span on the cars nearest the camera
      const P = this.uP.value, cam = this.ctx.camera.position;
      let best = a, bd = 1e18;
      for (let i = a; i <= b; i++) { const d = (P[i * 4] - cam.x) ** 2 + (P[i * 4 + 1] - cam.y) ** 2 + (P[i * 4 + 2] - cam.z) ** 2; if (d < bd) { bd = d; best = i; } }
      a = Math.max(0, Math.min(best - (this.maxHi >> 1), n - this.maxHi)); b = a + this.maxHi - 1;
    }
    if (!this.hi || !this.hiMesh || a < 0) {
      if (this.hiMesh) this.hiMesh.visible = false;
      this.loA.visible = true; this.loA.geometry.setDrawRange(0, lo.end[n - 1]);
      this.loB.visible = false;
      return;
    }
    this.hiMesh.visible = true;
    this.hiMesh.geometry.setDrawRange(this.hi.start[a], this.hi.end[b] - this.hi.start[a]);
    this.loA.visible = a > 0;
    if (a > 0) this.loA.geometry.setDrawRange(0, lo.start[a]);
    this.loB.visible = b < n - 1;
    if (b < n - 1) this.loB.geometry.setDrawRange(lo.start[b + 1], lo.end[n - 1] - lo.start[b + 1]);
  }

  private updateSteam(dt: number) {
    if (!this.steam || !this.aPuff || !this.aPuff2) return;
    const P = this.uP.value, Q = this.uQ.value;
    const step = Math.min(dt, 0.1);
    // emit
    for (let vi = 0; vi < this.vents.length; vi++) {
      const vt = this.vents[vi];
      this.ventAcc[vi] += vt.rate * step;
      while (this.ventAcc[vi] >= 1) {
        this.ventAcc[vi] -= 1;
        const o = vt.car * 4;
        this.q.set(Q[o], Q[o + 1], Q[o + 2], Q[o + 3]);
        this.v.set(vt.x, vt.y, vt.z).applyQuaternion(this.q);
        const h = this.puffHead; this.puffHead = (h + 1) % this.puffN;
        const jit = 0.4 * vt.size;
        this.puffPos[h * 3] = P[o] + this.v.x + (this.rand() - 0.5) * jit;
        this.puffPos[h * 3 + 1] = P[o + 1] + this.v.y + (this.rand() - 0.5) * jit;
        this.puffPos[h * 3 + 2] = P[o + 2] + this.v.z + (this.rand() - 0.5) * jit;
        // stack shoots up hard; joints and vents drift; the train flies on and leaves it behind
        const upv = vt.rate > 2 ? 7 : 2.2;
        this.v.set((this.rand() - 0.5) * 1.6, upv + this.rand() * 1.5, (this.rand() - 0.5) * 1.6).applyQuaternion(this.q);
        this.puffVel[h * 3] = this.v.x; this.puffVel[h * 3 + 1] = this.v.y; this.puffVel[h * 3 + 2] = this.v.z;
        this.puffAge[h] = 0;
        this.puffLife[h] = (vt.rate > 2 ? 10 : 4.6) * (0.75 + this.rand() * 0.5);
        this.puffSize[h] = vt.size * (0.8 + this.rand() * 0.5);
      }
    }
    // integrate + write
    const A = this.aPuff.array as Float32Array, B = this.aPuff2.array as Float32Array;
    const damp = Math.exp(-step * 0.7);
    for (let i = 0; i < this.puffN; i++) {
      const age = (this.puffAge[i] += step);
      const life = this.puffLife[i];
      if (age >= life) { A[i * 4 + 3] = 0; B[i * 2] = 0; continue; }
      this.puffVel[i * 3] *= damp; this.puffVel[i * 3 + 1] = this.puffVel[i * 3 + 1] * damp + 0.25 * step; this.puffVel[i * 3 + 2] *= damp;
      this.puffPos[i * 3] += this.puffVel[i * 3] * step;
      this.puffPos[i * 3 + 1] += this.puffVel[i * 3 + 1] * step;
      this.puffPos[i * 3 + 2] += this.puffVel[i * 3 + 2] * step;
      const t = age / life;
      A[i * 4] = this.puffPos[i * 3]; A[i * 4 + 1] = this.puffPos[i * 3 + 1]; A[i * 4 + 2] = this.puffPos[i * 3 + 2];
      A[i * 4 + 3] = this.puffSize[i] * (1 + 3.2 * Math.sqrt(t));
      B[i * 2] = Math.min(1, t * 8) * (1 - t) * (1 - t) * 0.85;
      B[i * 2 + 1] = (i * 0.618034) % 1;
    }
    this.aPuff.needsUpdate = true;
    this.aPuff2.needsUpdate = true;
  }

  dispose() { for (const d of this.disposables) d.dispose(); }
}
